/**
 * Track events from the store. These are fired up to the sdk layer to whatever tracking mechanism is implemented.
 */
import type { Middleware } from 'redux';
import * as NavigationActions from '../actions/navigation';
import * as InspirationActions from '../actions/inspiration';
import * as ConfigurationActions from '../actions/configuration';
import * as SceneActions from '../actions/scene';
import * as Logging from '../actions/logging';
import * as Core from '../selectors/core';
import * as FurnitureList from '../furnitureList';
import * as B from '../../data/bundling';

let trackEvent: (event: string, properties?: { [key: string]: any }) => void = () => {};
export const initialiseTracking = (
    trackEventHandler: (event: string, properties?: { [key: string]: any }) => void,
) => {
    trackEvent = trackEventHandler;
};

export const middleware: Middleware =
    ({ dispatch, getState }) =>
    next =>
    action => {
        const appState = getState();
        const product_brand = Core.range(appState);
        const currency = Core.currency(appState);

        const trackWithLogging = (event: string, properties?: { [key: string]: any }) => {
            trackEvent(event, properties);
            dispatch(Logging.logDebug('Track Event: ' + event, properties));
        };

        const unpackBasket = (products: { productId: string; quantity: number }[]) => {
            const result: string[] = [];
            products.forEach(product => {
                for (let i = 0; i < product.quantity; i++) {
                    result.push(product.productId);
                }
            });
            return result;
        };

        switch (action.type) {
            case NavigationActions.viewCatalogue:
                trackWithLogging('view_catalogue', {});
                break;

            case NavigationActions.modularSofaCreate:
                trackWithLogging('create_new_modular_furniture', {
                    product_category: 'sofa',
                    articles: ['sofa'],
                    product_brand,
                });
                break;

            case NavigationActions.modularSofaConfigureExisting:
                trackWithLogging('configure_modular_furniture_start', {
                    product_category: 'sofa',
                    articles: ['sofa'],
                    product_brand,
                    button: action.payload.from,
                });
                break;

            case NavigationActions.modularSofaUpdated:
                trackWithLogging('configure_modular_furniture_complete', {
                    product_category: 'sofa',
                    articles: ['sofa'],
                    product_brand,
                });
                break;

            case FurnitureList.addFurniture.type:
                trackWithLogging('add_furniture', {
                    product_ids: action.payload.productIds,
                    articles: action.payload.articleTypes,
                    product_brand,
                    trigger: action.payload.trigger,
                });
                break;

            case NavigationActions.removedFurniture:
                trackWithLogging('remove_furniture', {
                    product_ids: action.payload.productIds,
                    articles: action.payload.articles,
                    product_brand,
                    button: action.payload.from,
                });
                break;

            case SceneActions.furnitureInteractionComplete:
                trackWithLogging('interact_with_furniture', {
                    product_ids: action.payload.productIds,
                    product_brand,
                    interaction_type: action.payload.interactionType,
                });
                break;

            case NavigationActions.shareLinkCreated:
                trackWithLogging('share_link_created', {
                    location: action.payload.location,
                    furniture_count: action.payload.furnitureCount,
                });
                break;

            case NavigationActions.shareLinkCopied:
                trackWithLogging('share_link_copied');
                break;

            case NavigationActions.viewInAr:
                trackWithLogging('view_in_ar', { mode: action.payload.mode });
                break;

            case NavigationActions.floorSizeChangeStart:
                trackWithLogging('floor_size_edit_start');
                break;

            case NavigationActions.finalise:
                trackWithLogging('finalise', {
                    value: action.payload.value,
                    currency,
                    products: unpackBasket(action.payload.products),
                });
                break;

            case NavigationActions.initialLoadingFinished:
                if (action.payload.bundling) {
                    const bundling: B.Bundling = action.payload.bundling;
                    trackWithLogging('loading_finished', {
                        products_in_scene: B.singleProductIds(bundling),
                        bundles_in_scene: B.bundleIds(bundling),
                    });
                } else {
                    trackWithLogging('loading_finished');
                }

                break;

            case NavigationActions.exitViewShown:
                trackWithLogging('exit_view');
                break;

            case InspirationActions.inspirationDimensionsToggled:
                trackWithLogging('toggle_dimensions', {
                    toggle_action: action.payload.isVisible ? 'show' : 'hide',
                });
                break;

            case InspirationActions.openArDrawer:
                trackWithLogging('open_ar_drawer', {
                    item_id: action.payload.item_id,
                });
                break;

            case InspirationActions.closeArDrawer:
                trackWithLogging('close_ar_drawer', {
                    item_id: action.payload.item_id,
                });
                break;

            case InspirationActions.user3dSceneInteraction:
                trackWithLogging('scene_interaction');
                break;

            case InspirationActions.selectAttribute:
                trackWithLogging('select_attribute', action.payload);
                break;

            case InspirationActions.selectOption:
                trackWithLogging('select_option', action.payload);
                break;

            case ConfigurationActions.selectQuestion:
                trackWithLogging('configuration_question_view', {
                    category: action.payload.category,
                    type: action.payload.type,
                });
                break;

            case ConfigurationActions.selectProperty:
                trackWithLogging('configuration_selection_applied', {
                    category: action.payload.category,
                    type: action.payload.type,
                    new_option: action.payload.newOption,
                    previous_option: action.payload.previousOption,
                    selection_incompatible: !action.payload.choiceIsCompatible,
                });
                break;

            case ConfigurationActions.selectAttachmentOption:
                trackWithLogging('configuration_attachment_option_applied', {
                    type: action.payload.attachmentTag,
                    selection: action.payload.value,
                });
                break;

            case ConfigurationActions.user3dSceneInteraction:
                trackWithLogging('scene_interaction');
                break;

            case ConfigurationActions.undo:
                trackWithLogging('undo_redo', { type: 'undo' });
                break;

            case ConfigurationActions.redo:
                trackWithLogging('undo_redo', { type: 'redo' });
                break;

            case ConfigurationActions.share:
                trackWithLogging('share_view');
                break;

            case ConfigurationActions.error:
                trackWithLogging('error_displayed', {
                    type: action.payload.type,
                    reason: action.payload.reason,
                    productId: action.payload.productId,
                });
                break;

            case SceneActions.floorSizeUpdated:
                trackWithLogging('floor_size_edit_complete', {
                    original_dimensions: {
                        width: action.payload.originalDimensions.x,
                        height: action.payload.originalDimensions.y,
                    },
                    new_dimensions: {
                        width: action.payload.newDimensions.x,
                        height: action.payload.newDimensions.y,
                    },
                });
                break;

            case SceneActions.loadLayoutWithDiscontinuedProduct:
                trackWithLogging('layout_loaded_with_discontinued_product', {
                    discontinuedProductIds: action.payload.discontinuedProductIds,
                });
                break;
        }

        return next(action);
    };
